import { FormEvent, useEffect, useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Sheet, toast } from '@elbklar/ui';

import { SelectField, TextField, TextareaField } from '@/components/Felder';
import { radarApi } from '@/lib/api';
import { datum, hatFehler, hoechstens, httpsAdresse, nichtVor, ohne, pflicht, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import type { RadarDeadlineKind, RadarKind } from '@/types';
import { DEADLINE_KIND_TEXT, KIND_TEXT, optionen } from './radarText';

interface Props {
  offen: boolean;
  onSchliessen: () => void;
  onGespeichert: () => void;
  /** Vorbelegung aus dem gerade offenen Reiter. */
  art: RadarKind;
}

const LEER = {
  kind: 'foerderung' as RadarKind,
  title: '',
  provider: '',
  source_url: '',
  summary: '',
  deadline: '',
  deadline_kind: 'unbekannt' as RadarDeadlineKind,
  event_date: '',
  event_end_date: '',
  location: '',
};

type Feld = Exclude<keyof typeof LEER, 'kind' | 'deadline_kind'>;

/**
 * Ein Eintrag von Hand — der Tipp aus dem Netzwerk, der Newsletter, das
 * Gespräch auf der Messe. Alles, was der Roboter nicht findet.
 */
export function ProgramDialog({ offen, onSchliessen, onGespeichert, art }: Props) {
  const formId = useId();
  const [formular, setFormular] = useState({ ...LEER, kind: art });
  const [fehler, setFehler] = useState<Fehler<Feld>>({});

  useEffect(() => {
    if (!offen) return;
    setFormular({ ...LEER, kind: art });
    setFehler({});
  }, [offen, art]);

  /** Wert setzen und den Fehler des Felds zurücknehmen; ein neuer Beginn erledigt auch „Nicht vor dem Beginn“. */
  const setze = (feld: Feld, wert: string) => {
    setFormular({ ...formular, [feld]: wert });
    setFehler((f) => (feld === 'event_date' ? ohne(ohne(f, 'event_date'), 'event_end_date') : ohne(f, feld)));
  };

  const istEvent = formular.kind === 'event';

  const anlegen = useMutation({
    mutationFn: () =>
      radarApi.createProgram({
        kind: formular.kind,
        title: formular.title.trim(),
        provider: formular.provider.trim() || null,
        source_url: formular.source_url.trim() || null,
        summary: formular.summary.trim() || null,
        deadline: formular.deadline || null,
        deadline_kind: formular.deadline ? 'fest' : formular.deadline_kind,
        // Termin und Ort gibt es nur bei Veranstaltungen; was beim Wechsel der
        // Art noch in den ausgeblendeten Feldern steht, geht nicht mit.
        event_date: (istEvent && formular.event_date) || null,
        event_end_date: (istEvent && formular.event_end_date) || null,
        location: (istEvent && formular.location.trim()) || null,
      }),
    onSuccess: () => {
      toast.success('Eintrag angelegt');
      onGespeichert();
      onSchliessen();
    },
    // Bei einer 422 ist `detail` eine Liste von Objekten; als Kind eines
    // Toasts brächte React die Seite zur Fehlergrenze. Nur Text anzeigen.
    onError: (fehlschlag: { response?: { status?: number; data?: { detail?: unknown } } }) =>
      toast.error(
        fehlschlag.response?.status === 409
          ? 'Diesen Eintrag gibt es schon — gleiche Quelle oder gleicher Träger und Titel'
          : typeof fehlschlag.response?.data?.detail === 'string'
            ? fehlschlag.response.data.detail
            : 'Anlegen fehlgeschlagen'
      ),
  });

  // Grenzen aus RadarProgramCreate (finance-api); die Quelle zusätzlich mit
  // https://, sonst liest der Wochenlauf sie nicht.
  const absenden = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const unlesbar = unlesbareFelder(event.currentTarget);
    const neu: Fehler<Feld> = {
      title: pflicht(formular.title, 'Titel fehlt') ?? hoechstens(formular.title, 255),
      provider: hoechstens(formular.provider, 255),
      source_url: httpsAdresse(formular.source_url, { max: 1000 }),
      deadline: datum(formular.deadline, { leerErlaubt: true, unlesbar: unlesbar.has('deadline') }),
      ...(istEvent && {
        event_date: datum(formular.event_date, { leerErlaubt: true, unlesbar: unlesbar.has('event_date') }),
        event_end_date:
          datum(formular.event_end_date, { leerErlaubt: true, unlesbar: unlesbar.has('event_end_date') }) ??
          nichtVor(formular.event_end_date, formular.event_date, 'Nicht vor dem Beginn'),
        location: hoechstens(formular.location, 255),
      }),
    };
    setFehler(neu);
    if (hatFehler(neu)) {
      zumErstenFehler(event.currentTarget);
      return;
    }
    anlegen.mutate();
  };

  return (
    <Sheet
      open={offen}
      onOpenChange={(o) => !o && onSchliessen()}
      title="Eintrag anlegen"
      footer={
        <>
          <Button variant="ghost" onClick={onSchliessen} disabled={anlegen.isPending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={anlegen.isPending}>
            Eintrag anlegen
          </Button>
        </>
      }
    >
      <form noValidate id={formId} onSubmit={absenden} className="space-y-4">
        <SelectField
          label="Art"
          options={optionen(KIND_TEXT)}
          value={formular.kind}
          onChange={(e) => setFormular({ ...formular, kind: e.target.value as RadarKind })}
        />

        <TextField
          label="Titel"
          placeholder="Name des Programms"
          value={formular.title}
          onChange={(e) => setze('title', e.target.value)}
          error={fehler.title}
          required
        />

        <TextField
          label={istEvent ? 'Veranstalter' : 'Träger'}
          placeholder="z. B. SAB Sachsen"
          value={formular.provider}
          onChange={(e) => setze('provider', e.target.value)}
          error={fehler.provider}
        />

        <TextField
          label="Quelle (Link)"
          type="url"
          placeholder="https://…"
          hint="Wird automatisch überwacht, wenn die Seite lesbar ist"
          value={formular.source_url}
          onChange={(e) => setze('source_url', e.target.value)}
          error={fehler.source_url}
        />

        <TextareaField
          label="Zusammenfassung"
          rows={3}
          placeholder="Worum geht es, für wen, was gibt es?"
          value={formular.summary}
          onChange={(e) => setFormular({ ...formular, summary: e.target.value })}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label={istEvent ? 'Anmeldeschluss' : 'Frist'}
            type="date"
            name="deadline"
            value={formular.deadline}
            onChange={(e) => setze('deadline', e.target.value)}
            error={fehler.deadline}
          />
          <SelectField
            label="Fristart"
            options={optionen(DEADLINE_KIND_TEXT)}
            disabled={Boolean(formular.deadline)}
            hint={formular.deadline ? 'mit Datum: feste Frist' : undefined}
            value={formular.deadline ? 'fest' : formular.deadline_kind}
            onChange={(e) =>
              setFormular({
                ...formular,
                deadline_kind: e.target.value as RadarDeadlineKind,
              })
            }
          />
        </div>

        {istEvent && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField
                label="Datum"
                type="date"
                name="event_date"
                value={formular.event_date}
                onChange={(e) => setze('event_date', e.target.value)}
                error={fehler.event_date}
              />
              <TextField
                label="Ende"
                type="date"
                name="event_end_date"
                min={formular.event_date || undefined}
                value={formular.event_end_date}
                onChange={(e) => setze('event_end_date', e.target.value)}
                error={fehler.event_end_date}
              />
            </div>
            <TextField
              label="Ort"
              placeholder="z. B. Dresden, Messe"
              value={formular.location}
              onChange={(e) => setze('location', e.target.value)}
              error={fehler.location}
            />
          </>
        )}

      </form>
    </Sheet>
  );
}
